import React, {
	useEffect,
	useState
} from "react";
import { DataContext } from "../../../App";
import {
	Grid,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Button,
	Typography,
	TextField,
	Box,
} from "@mui/material";
import { getDisplayTime } from "../../../utils/date";
import { ViewButtonActions } from "../../Shared/ViewButton/ViewButton";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import Modal from "../../Shared/Modals/Modal";
import { AlertBox } from "../../Shared/Alert/Alert";
import { CREATE_USER_PERMISSION } from "../../../utils/permissionCatalogue";

export default function UserManagement({ seller, userdata }) {
	const context = React.useContext(DataContext);
	const canCreateUser = Boolean(userdata?.permissions?.has(CREATE_USER_PERMISSION));
	const canRemoveUser = canCreateUser;
	const canChangeRoles = canCreateUser;

	const [state, setState] = useState({
		users: [],
		roles: [],
		usersLoadError: "",
		usernameFilter: "",
		reloadTable: 0,
		isModalOpen: false,
		modalBodyTitle: "",
		modalBodyMessage1: "",
		modalBodyMessage1Data: [],
		modalBodyMessage2: "",
		modalBodyInputFields: {},
		modalButtonVariant1: "",
		modalButtonText1: "",
		modalButtonActionType1: "",
		modalButtonAction1: "",
		modalButtonVariant2: "",
		modalButtonText2: "",
		modalButtonActionType2: "",
		modalButtonAction2: "",
		modalButtonColor2: "primary",
		modalWarningMessage: "",
	});

	useEffect(() => {
		const getUsers = async () => {
			context.dataProvider.getAllUsers(seller)
				.then(response => {
					setState(previousState => {
						return {
							...previousState,
							users: response.data,
							usersLoadError: "",
						};
					});
				})
				.catch((error) => {
					const apiMessage = error?.response?.data?.message;
					const hasApiMessage = typeof apiMessage === "string" && apiMessage.trim() !== "";
					const status = error?.response?.status;
					let usersLoadError = "Unable to load users.";
					if (status === 403) {
						usersLoadError = hasApiMessage
							? apiMessage
							: "You do not have permission to view users.";
					} else if (hasApiMessage) {
						usersLoadError = apiMessage;
					}

					setState(previousState => {
						return {
							...previousState,
							users: [],
							usersLoadError,
						};
					});
				});
		};
		getUsers();
	}, [context.dataProvider, seller, state.reloadTable]);

	useEffect(() => {
		if (!seller || !canCreateUser || typeof context.dataProvider.getRoles !== "function") {
			return;
		}
		context.dataProvider.getRoles(seller)
			.then((response) => {
				setState((previousState) => ({
					...previousState,
					roles: Array.isArray(response?.data) ? response.data : [],
				}));
			})
			.catch(() => {
				setState((previousState) => ({
					...previousState,
					roles: [],
				}));
			});
	}, [context.dataProvider, seller, canCreateUser, state.reloadTable]);

	const roleOptions = () =>
		(state.roles || []).map((role) => ({
			value: role.id,
			label: role.is_system ? `${role.name} (system)` : role.name,
		}));

	const resetModal = () => {
		return (
			setState(previousState => {
				return {
					...previousState,
					isModalOpen: false,
					modalBodyTitle: "",
					modalBodyMessage1: "",
					modalBodyMessage2: "",
					modalBodyInputFields: {},
					modalButtonText1: "",
					modalButtonText2: "",
					modalWarningMessage: "",
				};
			})
		);
	};

	const onClickFetchEmailModal = async (username) => {

		resetModal();

		await context.dataProvider.getUserEmail(seller, username)
			.then(response => {
				setState(previousState => {
					return {
						...previousState,
						modalBodyTitle: `Email address found for ${username}`,
						modalBodyMessage1: response.data?.email,
						modalButtonVariant1: "contained",
						modalButtonText1: "Close",
						modalButtonActionType1: "close",
						modalButtonAction1: "",
					};
				});
			})
			.catch((error) => {
				const status = error?.response?.status;
				const apiMessage = error?.response?.data?.message;
				const hasApiMessage = typeof apiMessage === "string" && apiMessage.trim() !== "";
				const isForbidden = status === 403;

				let modalBodyTitle = `Email address not found for ${username}`;
				let modalBodyMessage1 = "Something went wrong, please contact support@bezos.ai";
				if (isForbidden) {
					modalBodyTitle = `Unable to get email for ${username}`;
					modalBodyMessage1 = hasApiMessage
						? apiMessage
						: "You do not have permission to view this user's email.";
				} else if (hasApiMessage) {
					modalBodyTitle = `Unable to get email for ${username}`;
					modalBodyMessage1 = apiMessage;
				}

				setState(previousState => {
					return {
						...previousState,
						modalBodyTitle,
						modalBodyMessage1,
						modalButtonVariant1: "contained",
						modalButtonText1: "Close",
						modalButtonActionType1: "close",
						modalButtonAction1: "",
					};
				});
			});

		setState(previousState => {
			return {
				...previousState,
				isModalOpen: true,
			};
		});
	};

	const onClickRemoveUser = async (username) => {

		resetModal();

		await context.dataProvider.removeUser(seller, username)
			.then(() => {
				setState(previousState => {
					return {
						...previousState,
						modalBodyTitle: `${username} has been removed`,
						modalBodyMessage1: `${username} no longer has access to the Seller Portal.`,
						modalBodyMessage2: "",
						modalButtonVariant1: "contained",
						modalButtonText1: "Done",
						modalButtonActionType1: "close",
						modalButtonAction1: "",
						reloadTable: previousState.reloadTable + 1,
					};
				});
			})
			.catch((error) => {
				const status = error?.response?.status;
				const apiMessage = error?.response?.data?.message;
				const hasApiMessage = typeof apiMessage === "string" && apiMessage.trim() !== "";

				let modalBodyMessage1 = "Something went wrong.";
				let modalBodyMessage2 = "Please try again, if this problem continues then contact support@bezos.ai";
				if (status === 403) {
					modalBodyMessage1 = hasApiMessage
						? apiMessage
						: "You do not have permission to remove users.";
					modalBodyMessage2 = "";
				} else if (hasApiMessage) {
					modalBodyMessage1 = apiMessage;
					modalBodyMessage2 = "";
				}

				setState(previousState => {
					return {
						...previousState,
						modalBodyTitle: `Unable to remove ${username}`,
						modalBodyMessage1,
						modalBodyMessage2,
						modalButtonVariant1: "contained",
						modalButtonText1: "Close",
						modalButtonActionType1: "close",
						modalButtonAction1: "",
					};
				});
			});

		setState(previousState => {
			return {
				...previousState,
				isModalOpen: true,
			};
		});
	};

	const onClickFetchRemoveUserModal = (username) => {
		setState(previousState => {
			return {
				...previousState,
				modalBodyTitle: `Remove ${username}?`,
				modalBodyMessage1: `This permanently removes ${username}'s access to the Seller Portal and deletes their login. This cannot be undone.`,
				modalBodyMessage2: "You can invite them again later using Add User.",
				modalBodyInputFields: {},
				modalButtonVariant1: "outlined",
				modalButtonText1: "Cancel",
				modalButtonActionType1: "close",
				modalButtonAction1: onModalClose,
				modalButtonVariant2: "contained",
				modalButtonText2: "Yes, Remove User",
				modalButtonActionType2: "function",
				modalButtonAction2: () => onClickRemoveUser(username),
				modalButtonColor2: "destructive",
				modalWarningMessage: "",
				isModalOpen: true,
			};
		});
	};

	const modalErrorMessageAddUser = (error) => {
		const status = error?.response?.status;
		const apiMessage = error?.response?.data?.message;
		const hasApiMessage = typeof apiMessage === "string" && apiMessage.trim() !== "";
		const isForbidden = status === 403;

		let modalBodyMessage1 = "Something went wrong.";
		let modalBodyMessage2 = "Please try again, if this problem continues then contact support@bezos.ai";
		if (isForbidden) {
			modalBodyMessage1 = hasApiMessage
				? apiMessage
				: "You do not have permission to create users.";
			modalBodyMessage2 = "";
		} else if (hasApiMessage) {
			modalBodyMessage1 = apiMessage;
			modalBodyMessage2 = "";
		}

		return (
			setState(previousState => {
				return {
					...previousState,
					modalBodyTitle: "User creation was unsuccessful",
					modalBodyMessage1,
					modalBodyMessage2,
					modalButtonVariant1: "contained",
					modalButtonText1: "Close",
					modalButtonActionType1: "close",
					modalButtonAction1: "",
				};
			})
		);
	};

	const parseRoleIDs = (value) => {
		if (!Array.isArray(value)) {
			return [];
		}
		return value.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
	};

	const onClickAddUserDetails = async (values) => {

		if (!seller) return;
		if (!values.user || !values.email) {
			setState(previousState => {
				return {
					...previousState,
					modalWarningMessage: "Please complete both fields",
					isModalOpen: true,
				};
			});
			return;
		}

		const roleIDs = parseRoleIDs(values.role_ids);
		resetModal();

		await context.dataProvider.createUser(seller, values.user, values.email, roleIDs)
			.then(response => {
				if (response) {
					setState(previousState => {
						return {
							...previousState,
							modalBodyTitle: `${values.user} successfully created`,
							modalBodyMessage1: `An email with subject "Your temporary password" has been sent to ${values.email}`,
							modalBodyMessage2: "Please login into the Seller Portal using this temporary password within 24 hours to activate the new user account.",
							modalButtonVariant1: "contained",
							modalButtonText1: "Done",
							modalButtonActionType1: "close",
							modalButtonAction1: "",
							reloadTable: previousState.reloadTable + 1,
						};
					});
				} else {
					modalErrorMessageAddUser();
				}
			})
			.catch((error) => {
				modalErrorMessageAddUser(error);
			});

		setState(previousState => {
			return {
				...previousState,
				isModalOpen: true,
				modalBodyInputFields: {},
			};
		});

	};

	const onClickFetchAddUserModal = async () => {
		const options = roleOptions();
		const sellerRole = (state.roles || []).find((role) => role.is_system && role.name === "seller");
		const defaultRoleIDs = sellerRole ? [sellerRole.id] : [];
		const fields = {
			user: {
				label: "Username",
				type: "text",
			},
			email: {
				label: "Email",
				type: "text",
			},
		};
		if (options.length > 0) {
			fields.role_ids = {
				label: "Roles",
				type: "multiselect",
				options,
				value: defaultRoleIDs,
			};
		}

		setState(previousState => {
			return {
				...previousState,
				modalBodyTitle: "Add a New User",
				modalBodyMessage1: "",
				modalBodyMessage2: "",
				modalBodyInputFields: fields,
				modalButtonVariant1: "outlined",
				modalButtonText1: "Cancel",
				modalButtonActionType1: "close",
				modalButtonAction1: onModalClose,
				modalButtonVariant2: "contained",
				modalButtonText2: "Save",
				modalButtonActionType2: "function",
				modalButtonAction2: onClickAddUserDetails,
				modalButtonColor2: "primary",
				isModalOpen: true,
			};
		});
	};

	const onClickUpdateUserRoles = async (username, values) => {
		const roleIDs = parseRoleIDs(values.role_ids);
		if (roleIDs.length === 0) {
			setState((previousState) => ({
				...previousState,
				modalWarningMessage: "Select at least one role",
				isModalOpen: true,
			}));
			return;
		}

		resetModal();

		await context.dataProvider.updateUserRoles(seller, username, roleIDs)
			.then(() => {
				setState((previousState) => ({
					...previousState,
					modalBodyTitle: `Roles updated for ${username}`,
					modalBodyMessage1: "The user's roles have been replaced.",
					modalBodyMessage2: "",
					modalButtonVariant1: "contained",
					modalButtonText1: "Done",
					modalButtonActionType1: "close",
					modalButtonAction1: "",
					reloadTable: previousState.reloadTable + 1,
				}));
			})
			.catch((error) => {
				const status = error?.response?.status;
				const apiMessage = error?.response?.data?.message;
				const hasApiMessage = typeof apiMessage === "string" && apiMessage.trim() !== "";
				let modalBodyMessage1 = "Something went wrong.";
				if (status === 403) {
					modalBodyMessage1 = hasApiMessage
						? apiMessage
						: "You do not have permission to change user roles.";
				} else if (hasApiMessage) {
					modalBodyMessage1 = apiMessage;
				}
				setState((previousState) => ({
					...previousState,
					modalBodyTitle: `Unable to update roles for ${username}`,
					modalBodyMessage1,
					modalBodyMessage2: "",
					modalButtonVariant1: "contained",
					modalButtonText1: "Close",
					modalButtonActionType1: "close",
					modalButtonAction1: "",
				}));
			});

		setState((previousState) => ({
			...previousState,
			isModalOpen: true,
			modalBodyInputFields: {},
		}));
	};

	const onClickFetchChangeRolesModal = (user) => {
		const selected = (state.roles || [])
			.filter((role) => (user.roles || []).includes(role.name))
			.map((role) => role.id);

		setState((previousState) => ({
			...previousState,
			modalBodyTitle: `Change roles for ${user.username}`,
			modalBodyMessage1: "This replaces the user's full role set.",
			modalBodyMessage2: "",
			modalBodyInputFields: {
				role_ids: {
					label: "Roles",
					type: "multiselect",
					options: roleOptions(),
					value: selected,
				},
			},
			modalButtonVariant1: "outlined",
			modalButtonText1: "Cancel",
			modalButtonActionType1: "close",
			modalButtonAction1: onModalClose,
			modalButtonVariant2: "contained",
			modalButtonText2: "Save",
			modalButtonActionType2: "function",
			modalButtonAction2: (values) => onClickUpdateUserRoles(user.username, values),
			modalButtonColor2: "primary",
			modalWarningMessage: "",
			isModalOpen: true,
		}));
	};

	const AddUserButton = () => {
		if (!canCreateUser) {
			return null;
		}

		return (
			<Button
				variant="outlined"
				color="secondary"
				className="reload-button"
				onClick={() => onClickFetchAddUserModal()}
				sx={{ mb:2 }}
			>
				Add User
			</Button>
		);
	};

	const PageHeading = () => {
		return (
			<Grid item xs={12}>
				<Typography component="h1" variant="h5" sx={{ mb:2 }}>
					Users
					<AddUserButton />
				</Typography>
			</Grid>
		);
	};

	const onModalClose = (props) => {
		setState(previousState => {
			return {
				...previousState,
				isModalOpen: props,
			};
		});
	};

	const rows = () => {
		if (!state.users) {
			return [];
		}

		const needle = (state.usernameFilter || "").trim().toLowerCase();
		const filtered = needle
			? state.users.filter((user) =>
				(user.username || "").toLowerCase().includes(needle))
			: state.users;

		return [...filtered].sort((a, b) => (a.username > b.username ? 1 : -1));
	};

	const rowActions = (user) => {
		const actions = [{
			title: "Get User Email",
			onClick: () => onClickFetchEmailModal(user.username),
		}];

		if (canChangeRoles && state.roles.length > 0 && user.username !== userdata?.user_name) {
			actions.push({
				title: "Change Roles",
				onClick: () => onClickFetchChangeRolesModal(user),
			});
		}

		if (canRemoveUser && user.username !== userdata?.user_name) {
			actions.push({
				title: "Remove User",
				onClick: () => onClickFetchRemoveUserModal(user.username),
				icon: <PersonRemoveIcon />,
				color: "error",
			});
		}

		return actions;
	};

	const visibleRows = rows();

	return (
		<Grid container sx={{ mb: 1 }}>

			<Grid item xs={12}>
				<Paper sx={{ p: 2, width: "100%" }} elevation={2}>
					<PageHeading />
					{state.usersLoadError && (
						<AlertBox severity="error" message={state.usersLoadError} sx={{ mb: 2 }} />
					)}
					<Box sx={{ mb: 2, maxWidth: 360 }}>
						<TextField
							label="Filter by username"
							size="small"
							fullWidth
							value={state.usernameFilter}
							onChange={(e) => setState((previousState) => ({
								...previousState,
								usernameFilter: e.target.value,
							}))}
							inputProps={{ "aria-label": "Filter by username" }}
						/>
					</Box>
					<TableContainer component={Paper}>
						<Table
							aria-label="user table"
							id="user-table"
							stickyHeader
							size={"small"}
							sx={{ mb:2 }}
						>
							<TableHead>
								<TableRow>
									<TableCell>Username</TableCell>
									<TableCell>Roles</TableCell>
									<TableCell>Created At</TableCell>
									<TableCell>Created By</TableCell>
									<TableCell></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{
									visibleRows.length > 0 ?
										visibleRows.map((u, index) =>
											<TableRow key={u.username || index}>
												<TableCell>
													{u.username}
												</TableCell>
												<TableCell>
													{(u.roles || []).toString()}
												</TableCell>
												<TableCell>
													{getDisplayTime(u.created_at)}
												</TableCell>
												<TableCell>
													{u.created_by}
												</TableCell>
												<TableCell title="More actions" align="right">
													<ViewButtonActions actions={rowActions(u)} />
												</TableCell>
											</TableRow>
										) :
										<TableRow>
											<TableCell colSpan={5} align="center">
												No users found
											</TableCell>
										</TableRow>
								}
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>
			</Grid>

			<Modal
				onModalClose={onModalClose}
				isModalOpen={state.isModalOpen}
				modalBodyTitle={state.modalBodyTitle}
				modalBodyMessage1={state.modalBodyMessage1}
				modalBodyMessage1Data={state.modalBodyMessage1Data}
				modalBodyMessage2={state.modalBodyMessage2}
				modalBodyInputFields={state.modalBodyInputFields}
				modalButtonVariant1={state.modalButtonVariant1}
				modalButtonText1={state.modalButtonText1}
				modalButtonActionType1={state.modalButtonActionType1}
				modalButtonAction1={state.modalButtonAction1}
				modalButtonVariant2={state.modalButtonVariant2}
				modalButtonText2={state.modalButtonText2}
				modalButtonActionType2={state.modalButtonActionType2}
				modalButtonAction2={state.modalButtonAction2}
				modalButtonColor2={state.modalButtonColor2}
				warningMessage={state.modalWarningMessage}
			/>

		</Grid>

	);
}
